import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { isAdmin } from "../utils/adminUtils";
import useListingCredits from "../hooks/useListingCredits";

// ToolTip component
const ToolTip = ({ toolTipLabel }) => {
    return (
        <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 bg-[#1E1E1E]/80 text-white text-sm py-1 px-3 rounded-md whitespace-nowrap shadow-lg transition-all duration-200">
            {toolTipLabel}
            {/* Arrow */}
            <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-[#1E1E1E]/80"></div>
        </div>
    );
};

// NavItem component
const NavItem = ({
    icon,
    label,
    tooltip,
    onClick,
    isSidebarOpen,
    comingSoon,
    active = false,
}) => {
    return (
        <li
            className={`relative group flex items-center py-2 rounded-lg cursor-pointer text-white
            ${comingSoon ? "" : ""}
            ${label ? "gap-4 px-3" : "justify-center"}
            `}
            onClick={onClick}
            aria-disabled={comingSoon}
            style={comingSoon ? { cursor: "not-allowed", opacity: 0.5 } : {}}
        >
            <div className="w-5 h-5 flex items-center justify-center">
                {icon}
            </div>
            {label && <span className="text-white leading-5 font-normal whitespace-nowrap">{label}</span>}

            {/* Tooltip */}
            {!isSidebarOpen && tooltip && <ToolTip toolTipLabel={tooltip} />}

            {/* Vertical line indicator */}
            <span
                className={`
                absolute right-0 h-6 w-[4px] bg-white
                transition-opacity
                ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
            `}
            />
        </li>
    );
};

const EmployerSidebar = ({ isOpen, onToggle, isMobile }) => {
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const navigate = useNavigate();
    const location = useLocation();
    const { credits, loading: creditsLoading } = useListingCredits();

    // CRITICAL: Role validation - Employer sidebar should ONLY render for Employers
    useEffect(() => {
        if (!user) return;
        if (profileInfo && profileInfo.role !== 'Employer') {
            console.warn(`[EmployerSidebar] Access denied: User role is ${profileInfo.role}, redirecting`);
            if (profileInfo.role === 'Employee') {
                navigate("/employee/dashboard", { replace: true });
            } else {
                navigate("/role-selection", { replace: true });
            }
        }
    }, [user, profileInfo, navigate]);

    // Get current plan from user metadata or default to "Free Starter"
    const metadataPlan = user?.user_metadata?.subscription_plan || "Free Starter";
    const currentPlan = credits
        ? (credits.isFreePlan ? "Free Starter" : "Sponsored Jobs")
        : metadataPlan;

    const planDetail = credits
        ? (credits.remainingListings >= 999999
            ? "Unlimited listings"
            : `${credits.remainingListings} listings available`)
        : creditsLoading
            ? "Checking plan status..."
            : "View plans";
    const isSponsoredPlan = !!credits && !credits.isFreePlan;

    if (!user) {
        return null;
    }

    // CRITICAL: Don't render sidebar if user is not an Employer
    if (profileInfo && profileInfo.role !== 'Employer') {
        return null;
    }

    return (
        <nav
            className={`flex flex-col text-white ${
                isMobile ? "w-[270px]" : (!isOpen ? "w-[78px]" : "w-[270px]")
            } h-screen`}
            style={{ backgroundColor: '#1e1e1e' }}
        >
            {/* Logo section */}
            <header
                className={`flex items-center ${
                    (isMobile || isOpen) ? "gap-5 px-6 py-5" : "justify-center py-5"
                }`}
                style={{ backgroundColor: '#1e1e1e' }}
            >
                {(isMobile || isOpen) && (
                    <div className="flex items-center gap-3">
                        <img
                            alt="Doctors' Jobs Logo"
                            src="/DJ logo white.svg"
                            className="flex-shrink-0 h-8 w-auto object-contain cursor-pointer"
                            onClick={() => {
                                navigate("/");
                                if (isMobile) {
                                    onToggle();
                                }
                            }}
                        />
                    </div>
                )}
                {!isMobile && !isOpen && (
                    <img
                        alt="Open Sidebar"
                        src="/minimise-close.svg"
                        className="w-6 h-6 object-contain cursor-pointer transform scale-x-[-1]"
                        onClick={onToggle}
                    />
                )}
                {(isMobile || isOpen) && (
                    <div
                        className={`transition-transform duration-300`}
                    >
                        <img
                            onClick={onToggle}
                            alt="Minimise Close"
                            src="/minimise-close.svg"
                            className="w-6 h-6 object-contain cursor-pointer"
                        />
                    </div>
                )}
            </header>

            {/* Divider underneath header */}
            <div className="border-t border-gray-600"></div>

            {/* Main menu */}
            <div className={`flex-grow ${(!isMobile && !isOpen) ? "" : "overflow-y-auto"}`}>
                <ul
                    className={`flex flex-col gap-1 px-4 mt-5 ${
                        (!isMobile && !isOpen) ? "px-2" : ""
                    }`}
                >

                    <NavItem
                        icon={
                            <img
                                alt="Dashboard"
                                src="/House.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Dashboard" : ""}
                        tooltip="Dashboard"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employer') {
                                console.warn('[EmployerSidebar] Navigation blocked: User is not an Employer');
                                return;
                            }
                            navigate("/employer/dashboard");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employer/dashboard"}
                    />

                    <NavItem
                        icon={
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                            </svg>
                        }
                        label={(isMobile || isOpen) ? "My Job posts" : ""}
                        tooltip="My Job posts"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employer') {
                                console.warn('[EmployerSidebar] Navigation blocked: User is not an Employer');
                                return;
                            }
                            navigate("/employer/jobs");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employer/jobs"}
                    />

                    <NavItem
                        icon={
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z"/>
                            </svg>
                        }
                        label={(isMobile || isOpen) ? "Hiring Pipeline" : ""}
                        tooltip="Hiring Pipeline"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employer') {
                                console.warn('[EmployerSidebar] Navigation blocked: User is not an Employer');
                                return;
                            }
                            navigate("/employer/hiring-pipeline");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employer/hiring-pipeline"}
                    />

                    <NavItem
                        icon={
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                            </svg>
                        }
                        label={(isMobile || isOpen) ? "Job Candidates" : ""}
                        tooltip="Job Candidates"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employer') {
                                console.warn('[EmployerSidebar] Navigation blocked: User is not an Employer');
                                return;
                            }
                            navigate("/employer/candidates");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/employer/candidates"}
                    />

                </ul>

                {/* Divider line */}
                <div className="px-4 my-4">
                    <div className="border-t border-gray-600"></div>
                </div>

                {/* Admin Dashboard Link - Only visible to admin users */}
                {isAdmin(user) && (
                    <ul
                        className={`flex flex-col gap-1 px-4 ${
                            (!isMobile && !isOpen) ? "px-2" : ""
                        }`}
                    >
                        <NavItem
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.9 15.8,12.6 15.8,13.3V16.5C15.8,17.9 14.4,19.3 13,19.3H11C9.6,19.3 8.2,17.9 8.2,16.5V13.3C8.2,12.6 8.6,11.9 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11.5H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z"/>
                                </svg>
                            }
                            label={(isMobile || isOpen) ? "Admin Dashboard" : ""}
                            tooltip="Admin Dashboard"
                            isSidebarOpen={isMobile || isOpen}
                            onClick={() => {
                                navigate("/admin/hpcsa-verification");
                                if (isMobile) {
                                    onToggle();
                                }
                            }}
                            active={location.pathname === "/admin/hpcsa-verification"}
                        />
                    </ul>
                )}
            </div>

            {/* Footer */}
            <footer className="mt-auto px-4 py-4 flex flex-col gap-3">
                {/* Divider line above Suggest Ideas */}
                <div className="border-t border-gray-600"></div>

                {/* Suggest Ideas */}
                {(isMobile || isOpen) && (
                    <div className="flex items-center justify-between bg-black rounded-lg border border-gray-600 p-4 cursor-pointer hover:bg-gray-800 transition-colors">
                        <div className="flex items-center gap-3">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                            </svg>
                            <span className="font-normal text-white">Suggest ideas</span>
                        </div>
                        <span className="text-white text-lg font-bold">+</span>
                    </div>
                )}

                {/* Current Plan */}
                {(isMobile || isOpen) && (
                    <div 
                        className="bg-white rounded-lg border border-gray-300 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => {
                            // CRITICAL: Double-check role before navigation
                            if (profileInfo?.role !== 'Employer') {
                                console.warn('[EmployerSidebar] Navigation blocked: User is not an Employer');
                                return;
                            }
                            navigate("/employer/pricing");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <span className="font-normal text-black">Current Plan</span>
                                <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                                </svg>
                            </div>
                            {isSponsoredPlan && (
                                <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                    Sponsored
                                </span>
                            )}
                        </div>
                        <div className="text-xl font-bold text-black mb-1">{currentPlan}</div>
                        <div className="text-sm text-gray-600 mb-3">{planDetail}</div>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>{credits ? "Manage plan" : "View plans"}</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                    </div>
                )}

                {/* User Profile */}
                {(isMobile || isOpen) && user && (
                    <div className="bg-gray-600 rounded-lg p-3 cursor-pointer hover:bg-gray-500 transition-colors">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-white font-bold text-sm">
                                    {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                                </div>
                                <div className="text-white text-xs">
                                    {user.email}
                                </div>
                            </div>
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                            </svg>
                        </div>
                    </div>
                )}
                
                {/* Collapsed User Profile */}
                {!isMobile && !isOpen && user && (
                    <div className="relative group flex justify-center">
                        <div 
                            className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center"
                        >
                            <span className="text-white text-sm font-medium">
                                {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                            </span>
                        </div>
                        
                        {/* Tooltip */}
                        <ToolTip toolTipLabel={`${user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}`} />
                    </div>
                )}
            </footer>

        </nav>
    );
};

export default EmployerSidebar;
